(() => {
  if (window.EthnicWishDiscovery) { window.EthnicWishDiscovery.init(); return; }
  const mounted = new Map();
  const historyKey = 'ethnicwish-recent-products-v1';
  const initPhotos = root => {
    root.querySelectorAll('[data-card-photos]').forEach(gallery => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      let sources;
      try { sources = JSON.parse(gallery.querySelector('[data-card-photo-sources]')?.textContent || '[]'); } catch (_) { return; }
      if (!Array.isArray(sources) || sources.length < 2 || sources.some(src => typeof src !== 'string' || !src)) return;
      const link = gallery.querySelector('[data-card-photo-link]');
      const img = link.querySelector('img');
      if (!img) return;
      const dots = gallery.querySelector('[data-card-photo-dots]');
      const track = document.createElement('span');
      track.className = 'ew-discovery-card__photo-track';
      const buttons = [];
      let suppressUntil = 0;
      let drag;
      sources.forEach((src, position) => {
        const slide = document.createElement('span');
        slide.className = 'ew-discovery-card__photo-slide';
        const photo = position === 0 ? img : img.cloneNode(false);
        if (position > 0) {
          const url = new URL(src, location.href);
          if (!['https:', 'http:'].includes(url.protocol)) return;
          url.searchParams.set('width', '900');
          photo.removeAttribute('srcset');
          photo.src = url.href;
          photo.loading = 'lazy';
          photo.decoding = 'async';
        }
        photo.draggable = false;
        slide.appendChild(photo);
        track.appendChild(slide);
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', gallery.dataset.photoLabel.replace('__INDEX__', position + 1));
        button.setAttribute('aria-pressed', String(position === 0));
        button.addEventListener('click', () => {
          track.scrollTo({ left: position * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        });
        dots.appendChild(button);
        buttons.push(button);
      });
      link.appendChild(track);
      track.addEventListener('scroll', () => {
        const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        buttons.forEach((button, position) => button.setAttribute('aria-pressed', String(position === index)));
        suppressUntil = Date.now() + 400;
      }, { passive: true });
      track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      });
      track.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        event.preventDefault();
        track.scrollLeft = drag.left - dx;
        suppressUntil = Date.now() + 400;
      });
      const finishDrag = event => {
        if (!drag || drag.id !== event.pointerId) return;
        const moved = drag.moved;
        const targetLeft = Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) * track.clientWidth;
        drag = null;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        if (moved) {
          suppressUntil = Date.now() + 400;
          track.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
      };
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('lostpointercapture', finishDrag);
      track.addEventListener('pointerleave', event => { if (drag && !drag.moved) finishDrag(event); });
      gallery.addEventListener('dragstart', event => event.preventDefault());
      link.addEventListener('click', event => { if (Date.now() < suppressUntil) event.preventDefault(); });
      gallery.querySelector('[data-card-photo-controls]').hidden = false;
    });
  };
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const readHistory = () => {
    try {
      const value = JSON.parse(localStorage.getItem(historyKey));
      return Array.isArray(value) ? [...new Set(value.filter(validHandle))].slice(0, 20) : [];
    } catch (_) { return []; }
  };
  const init = () => {
    document.querySelectorAll('[data-product-discovery]').forEach(root => {
      if (mounted.has(root)) return;
      const cleanups = [];
      const controllers = new Set();
      mounted.set(root, () => { cleanups.forEach(fn => fn()); controllers.forEach(controller => controller.abort()); });
      const current = root.dataset.productHandle;
      const limit = Math.min(12, Math.max(4, Number(root.dataset.limit) || 8));
      const editor = root.dataset.editor === 'true';
      const recentRail = root.querySelector('[data-recent-rail]');
      const bestRail = root.querySelector('[data-best-rail]');
      const previous = readHistory().filter(handle => handle !== current).slice(0, limit);
      if (recentRail && validHandle(current)) {
        try { localStorage.setItem(historyKey, JSON.stringify([current, ...readHistory().filter(handle => handle !== current)].slice(0, 20))); } catch (_) {}
      }
      const syncers = new Map();
      root.querySelectorAll('[data-discovery-rail]').forEach(rail => {
        const track = rail.querySelector('[data-discovery-track]');
        const controls = rail.querySelector('[data-discovery-controls]');
        const prev = rail.querySelector('[data-discovery-prev]');
        const next = rail.querySelector('[data-discovery-next]');
        const sync = () => {
          const max = track.scrollWidth - track.clientWidth;
          controls.hidden = max <= 2;
          prev.disabled = Math.abs(track.scrollLeft) <= 2;
          next.disabled = Math.abs(track.scrollLeft) >= max - 2;
        };
        const move = direction => {
          const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
          track.scrollBy({ left: direction * rtl * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        };
        prev.addEventListener('click', () => move(-1));
        next.addEventListener('click', () => move(1));
        track.addEventListener('scroll', sync, { passive: true });
        const resize = new ResizeObserver(sync);
        resize.observe(track);
        cleanups.push(() => resize.disconnect());
        syncers.set(rail, sync);
        sync();
      });
      const fetchCards = async path => {
        const url = new URL(path, location.origin);
        if (url.origin !== location.origin) return [];
        url.searchParams.set('section_id', 'product-slider-cards');
        const controller = new AbortController();
        controllers.add(controller);
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) return [];
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          return Array.from(html.querySelectorAll('[data-discovery-item]'));
        } catch (_) { return []; }
        finally { clearTimeout(timer); controllers.delete(controller); }
      };
      const fill = (rail, cards) => {
        if (!root.isConnected) return;
        const unique = new Set();
        const items = cards.filter(card => {
          const handle = card.dataset.handle;
          if (!validHandle(handle) || handle === current || unique.has(handle)) return false;
          unique.add(handle);
          card.setAttribute('role', 'listitem');
          return true;
        }).slice(0, limit);
        rail.querySelector('[data-discovery-track]').replaceChildren(...items);
        initPhotos(rail);
        window.EthnicWishWishlist?.syncUI();
        rail.hidden = !items.length && !editor;
        const empty = rail.querySelector('[data-discovery-empty]');
        if (empty) empty.hidden = items.length > 0;
        window.EthnicWishCurrency?.refresh(rail);
        syncers.get(rail)?.();
      };
      const load = async () => {
        if (recentRail) {
          if (!previous.length) fill(recentRail, []);
          else {
            const results = new Array(previous.length);
            let index = 0;
            const worker = async () => {
              while (index < previous.length && root.isConnected) {
                const position = index++;
                const handle = previous[position];
                const cards = await fetchCards(`${root.dataset.productsUrl}${encodeURIComponent(handle)}`);
                results[position] = cards.find(card => card.dataset.handle === handle);
              }
            };
            Promise.all([worker(), worker(), worker()]).then(() => fill(recentRail, results.filter(Boolean)));
          }
        }
        if (bestRail) {
          const url = new URL(bestRail.dataset.collectionUrl, location.origin);
          url.searchParams.set('sort_by', 'best-selling');
          fill(bestRail, await fetchCards(url));
        }
      };
      if (editor || !('IntersectionObserver' in window)) load();
      else {
        const observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); }
        }, { rootMargin: '500px' });
        observer.observe(root);
        cleanups.push(() => observer.disconnect());
      }
    });
  };
  window.EthnicWishDiscovery = { init };
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    mounted.forEach((dispose, root) => {
      if (event.target.contains(root)) { dispose(); mounted.delete(root); }
    });
  });
  document.addEventListener('shopify:block:select', event => {
    if (event.target.closest('.ew-discovery__review')) event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  });
  init();
})();
